import { useState, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import Container from '../../../components/ui/Container/Container';
import { AlertMessage } from '../../../components/ui/AlertMessage/AlertMessage';
import { AlertStatus } from '../../../components/ui/AlertMessage/AlertStatus';
import { useStoreContext } from '../../../store/store';
import {
  IFileUploadResult,
  IApiResponse,
  IDirectoryCreateRequest
} from '../../../utils/constants/interfaces';
import useFileManager from './useFileManager';
import './FileManagerList.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faDownload,
  faTrash,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

export const FileManagerList = () => {
  const manager = useFileManager();
  const { user } = useStoreContext();

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create folder state
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // Delete file state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ path: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // File conflict state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingFile, setConflictingFile] = useState<File | null>(null);
  const [newFileName, setNewFileName] = useState('');

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setUploadMessage('');

    try {
      const formData = new FormData();
      formData.append('directoryPath', manager.currentDirectory);
      formData.append('file', file);

      const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';

      // Use fetch directly for FormData to ensure proper multipart/form-data handling
      const response = await fetch(`${apiUrl}/files/upload`, {
        method: 'POST',
        body: formData,
        headers: user.token
          ? {
              Authorization: `Bearer ${user.token}`
            }
          : {}
      });

      const result = (await response.json()) as IApiResponse<IFileUploadResult>;

      if (result.status.statusCode === 200 && result.response?.succeeded) {
        setUploadMessage(`Successfully uploaded ${file.name}`);
        // Refresh the file list
        await manager.loadDirectory(manager.currentDirectory);
      } else {
        setUploadMessage(result.response?.message || 'Upload failed');
      }
    } catch (error) {
      setUploadMessage(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = (file: File) => {
    // Check if a file with the same name already exists in the current directory
    const existingFile = manager.files.find((f) => f.name === file.name);

    if (existingFile) {
      // File exists, show conflict modal
      setConflictingFile(file);
      setNewFileName(file.name);
      setShowConflictModal(true);
    } else {
      // No conflict, upload directly
      void uploadFile(file);
    }
  };

  const resolveFileConflict = async (action: 'overwrite' | 'rename') => {
    if (!conflictingFile) return;

    if (action === 'overwrite') {
      // Upload with original name (will overwrite)
      await uploadFile(conflictingFile);
    } else if (action === 'rename') {
      // Create a new File object with the new name
      const renamedFile = new File([conflictingFile], newFileName, {
        type: conflictingFile.type,
        lastModified: conflictingFile.lastModified
      });
      await uploadFile(renamedFile);
    }

    // Close modal and reset state
    setShowConflictModal(false);
    setConflictingFile(null);
    setNewFileName('');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadFile = async (filePath: string, fileName: string) => {
    try {
      const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';

      // Encode path segments individually to preserve forward slashes
      const encodedPath = filePath
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');

      const response = await fetch(`${apiUrl}/files/download/${encodedPath}`, {
        method: 'GET',
        headers: user.token
          ? {
              Authorization: `Bearer ${user.token}`
            }
          : {}
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setUploadMessage(
        `Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const deleteFile = async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    setUploadMessage('');

    try {
      const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';

      // Encode path segments individually to preserve forward slashes
      const encodedPath = fileToDelete.path
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');

      const response = await fetch(`${apiUrl}/files/${encodedPath}`, {
        method: 'DELETE',
        headers: user.token
          ? {
              Authorization: `Bearer ${user.token}`
            }
          : {}
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      setUploadMessage(`Successfully deleted ${fileToDelete.name}`);
      setShowDeleteModal(false);
      setFileToDelete(null);
      // Refresh the file list
      await manager.loadDirectory(manager.currentDirectory);
    } catch (error) {
      setUploadMessage(
        `Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      setUploadMessage('Please enter a folder name');
      return;
    }

    setIsCreatingFolder(true);
    setUploadMessage('');

    try {
      const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';

      // Construct the full directory path
      const directoryPath = manager.currentDirectory
        ? `${manager.currentDirectory}/${newFolderName.trim()}`
        : newFolderName.trim();

      const requestBody: IDirectoryCreateRequest = {
        directoryPath,
        createdBy: user.userName || user.userId || undefined
      };

      const response = await fetch(`${apiUrl}/files/directory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user.token
            ? {
                Authorization: `Bearer ${user.token}`
              }
            : {})
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setUploadMessage(`Successfully created folder "${newFolderName}"`);
        setNewFolderName('');
        setShowCreateFolderModal(false);
        // Refresh the file list to show the new folder
        await manager.loadDirectory(manager.currentDirectory);
      } else {
        try {
          const errorData = (await response.json()) as unknown;
          const message =
            errorData && typeof errorData === 'object' && 'message' in errorData
              ? (errorData as { message: string }).message
              : 'Failed to create folder';
          setUploadMessage(message);
        } catch {
          setUploadMessage('Failed to create folder');
        }
      }
    } catch (error) {
      setUploadMessage(
        `Failed to create folder: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsCreatingFolder(false);
    }
  };

  return (
    <div className="page-container mt-2">
      {/* Breadcrumb Navigation */}
      <div className="mb-3">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <button
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => manager.loadDirectory('')}
                disabled={manager.loading}>
                Root
              </button>
            </li>
            {manager.currentDirectory &&
              manager.currentDirectory.split('/').map((part, index, array) => {
                const path = array.slice(0, index + 1).join('/');
                return (
                  <li key={path} className="breadcrumb-item">
                    {index === array.length - 1 ? (
                      part
                    ) : (
                      <button
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={() => manager.loadDirectory(path)}
                        disabled={manager.loading}>
                        {part}
                      </button>
                    )}
                  </li>
                );
              })}
          </ol>
        </nav>
      </div>

      {/* Upload Message */}
      {uploadMessage && (
        <AlertMessage
          status={uploadMessage.includes('Successfully') ? AlertStatus.SUCCESS : AlertStatus.ERROR}
          message={uploadMessage}
          visible={!!uploadMessage}
        />
      )}

      {/* Error Message */}
      {manager.error && (
        <div className="alert alert-danger" role="alert">
          {manager.error}
        </div>
      )}

      {/* Drag and Drop Upload Area */}
      <Container className="mb-4">
        <div
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept="*/*"
          />

          <div className="text-center py-4">
            <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" className="mb-3 text-muted" />
            <h5 className="mb-2">
              {isUploading ? 'Uploading...' : 'Drop files here or click to upload'}
            </h5>
            <p className="text-muted mb-0">
              {isUploading
                ? 'Please wait while your file is being uploaded...'
                : 'Upload files to the current directory'}
            </p>
          </div>
        </div>
      </Container>

      {/* Directory Contents */}
      <Container>
        <LoadingContainer isLoading={manager.loading}>
          <div className="file-browser-container">
            {/* Current Directory Header */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h5 className="mb-1">
                  <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2 text-primary" />
                  {manager.currentDirectory || 'Root'}
                </h5>
                <small className="text-muted">
                  {manager.directories.length + manager.files.length} items
                </small>
              </div>
              <div>
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={() => setShowCreateFolderModal(true)}
                  title="Create New Folder">
                  <FontAwesomeIcon icon={faCloudUploadAlt} className="me-1" />
                  New Folder
                </button>
              </div>
            </div>

            {/* File Browser Table */}
            {manager.directories.length > 0 ||
            manager.files.length > 0 ||
            manager.hasParentDirectory ? (
              <div className="file-browser-table">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="border-0 fw-normal text-muted" style={{ width: '50px' }}></th>
                      <th className="border-0 fw-normal text-muted">Name</th>
                      <th className="border-0 fw-normal text-muted d-none d-md-table-cell">Size</th>
                      <th className="border-0 fw-normal text-muted d-none d-lg-table-cell">Type</th>
                      <th className="border-0 fw-normal text-muted d-none d-lg-table-cell">
                        Modified
                      </th>
                      <th className="border-0 fw-normal text-muted d-none d-xl-table-cell">
                        Uploaded By
                      </th>
                      <th className="border-0 fw-normal text-muted" style={{ width: '100px' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Parent Directory (if not root) */}
                    {manager.hasParentDirectory && (
                      <tr
                        className="file-row parent-directory"
                        onClick={manager.navigateUp}
                        style={{ cursor: 'pointer' }}>
                        <td>
                          <FontAwesomeIcon icon={faCloudUploadAlt} className="text-muted" />
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="text-primary fw-medium">..</span>
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell text-muted">-</td>
                        <td className="d-none d-lg-table-cell text-muted">Parent Directory</td>
                        <td className="d-none d-lg-table-cell text-muted">-</td>
                        <td className="d-none d-xl-table-cell text-muted">-</td>
                        <td></td>
                      </tr>
                    )}

                    {/* Directories */}
                    {manager.directories.map((directory) => {
                      const directoryName = directory.split('/').pop() || directory;
                      return (
                        <tr
                          key={directory}
                          className="file-row directory-row"
                          onClick={() => manager.navigateToDirectory(directory)}
                          style={{ cursor: 'pointer' }}>
                          <td>
                            <i className="fas fa-folder text-warning fa-lg"></i>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="text-primary fw-medium">{directoryName}</span>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell text-muted">-</td>
                          <td className="d-none d-lg-table-cell text-muted">Directory</td>
                          <td className="d-none d-lg-table-cell text-muted">-</td>
                          <td className="d-none d-xl-table-cell text-muted">-</td>
                          <td></td>
                        </tr>
                      );
                    })}

                    {/* Files */}
                    {manager.files.map((file) => (
                      <tr key={file.path} className="file-row">
                        <td>
                          <i className="fas fa-file text-secondary"></i>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span>{file.name}</span>
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell">{formatFileSize(file.size)}</td>
                        <td className="d-none d-lg-table-cell">{file.contentType}</td>
                        <td className="d-none d-lg-table-cell">{formatDate(file.lastModified)}</td>
                        <td className="d-none d-xl-table-cell">{file.uploadedBy}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => downloadFile(file.path, file.name)}
                              title="Download file">
                              <FontAwesomeIcon icon={faDownload} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setFileToDelete({ path: file.path, name: file.name });
                                setShowDeleteModal(true);
                              }}
                              title="Delete file">
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Empty State */
              <div className="text-center py-5">
                <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">This directory is empty</h5>
                <p className="text-muted">No files or subdirectories found.</p>
              </div>
            )}
          </div>
        </LoadingContainer>
      </Container>

      {/* Create Folder Modal */}
      <Modal
        show={showCreateFolderModal}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered={true}
        onHide={() => setShowCreateFolderModal(false)}>
        <Modal.Header closeButton={true}>
          <Modal.Title>
            <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2" />
            Create New Folder
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label htmlFor="folderName" className="form-label">
              Folder Name
            </label>
            <input
              id="folderName"
              type="text"
              className="form-control"
              placeholder="Enter folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isCreatingFolder) {
                  void createFolder();
                }
              }}
              autoFocus
            />
            <small className="text-muted">
              Folder will be created in: {manager.currentDirectory || 'Root'}
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <button
            className="btn btn-secondary"
            disabled={isCreatingFolder}
            onClick={() => {
              setShowCreateFolderModal(false);
              setNewFolderName('');
            }}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!newFolderName.trim() || isCreatingFolder}
            onClick={createFolder}>
            <FontAwesomeIcon icon={faCloudUploadAlt} className="me-1" />
            Create Folder
            {isCreatingFolder && <Spinner size="sm" animation="border" className="ms-2" />}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered={true}
        onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton={true}>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className="me-2 text-danger" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FontAwesomeIcon icon={faTrash} size="3x" className="text-danger mb-3" />
            </div>
            <h5 className="mb-3">Are you sure you want to delete this file?</h5>
            <p className="text-muted mb-0">
              <strong>{fileToDelete?.name}</strong>
            </p>
            <p className="text-muted mt-2">
              This action cannot be undone. The file will be permanently removed from the server.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <button
            className="btn btn-secondary"
            disabled={isDeleting}
            onClick={() => {
              setShowDeleteModal(false);
              setFileToDelete(null);
            }}>
            Cancel
          </button>
          <button className="btn btn-danger" disabled={isDeleting} onClick={deleteFile}>
            <FontAwesomeIcon icon={faTrash} className="me-1" />
            Delete File
            {isDeleting && <Spinner size="sm" animation="border" className="ms-2" />}
          </button>
        </Modal.Footer>
      </Modal>

      {/* File Conflict Modal */}
      <Modal
        show={showConflictModal}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered={true}
        onHide={() => setShowConflictModal(false)}>
        <Modal.Header closeButton={true}>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            File Already Exists
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div className="mb-3">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size="3x"
                className="text-warning mb-3"
              />
            </div>
            <h5 className="mb-3">A file with this name already exists</h5>
            <p className="text-muted mb-4">
              <strong>{conflictingFile?.name}</strong> already exists in this directory.
            </p>
          </div>

          <div className="mb-3">
            <label htmlFor="conflictFileName" className="form-label">
              New Filename (optional)
            </label>
            <input
              id="conflictFileName"
              type="text"
              className="form-control"
              placeholder="Enter a new filename"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              autoFocus
            />
            <small className="text-muted">
              Leave unchanged to overwrite, or modify to create a new file
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowConflictModal(false);
              setConflictingFile(null);
              setNewFileName('');
            }}>
            Cancel
          </button>
          <div>
            <button
              className="btn btn-outline-primary me-2"
              onClick={() => resolveFileConflict('rename')}
              disabled={!newFileName.trim() || newFileName === conflictingFile?.name}>
              <FontAwesomeIcon icon={faCloudUploadAlt} className="me-1" />
              Upload as &quot;{newFileName}&quot;
            </button>
            <button className="btn btn-warning" onClick={() => resolveFileConflict('overwrite')}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
              Overwrite Existing
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
